/**
 * Review your order — the last step before money moves.
 *
 * Everything the customer is agreeing to, in one place: the meal, the days
 * and when it is served, what is in each box and how many boxes, how it
 * reaches them, who the kitchen calls, and the total — re-quoted on arrival, so the figure they
 * confirm is the one the server will charge. If lunch closes while they read,
 * the order endpoint refuses it and the reason is shown here.
 */

import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  formatDayRange,
  formatDays,
  formatMeals,
  formatMoney,
  formatPeople,
  formatWindow,
  serviceScheduleLabel,
  tiffinFulfilmentLabel,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardDivider, CardRow } from '../../components/common/Card';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { TIFFIN_STEPS } from '../../components/donation/CheckoutStepper';
import { Glyph, LockIcon } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { useScheduleDays } from '../../components/tiffin/SchedulePicker';
import { Text } from '../../components/common/Text';
import {
  useAppConfig,
  useCreateTiffinOrder,
  useInitiatePayment,
  usePaymentMethods,
  useTiffinQuote,
} from '../../core/api/hooks';
import { toCreateTiffinRequest, useTiffinStore } from './tiffinStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';
import { paymentReturnUrl } from '../../core/payments/hostedPage';

export function TiffinReviewScreen({ navigation }: DonorScreenProps<'TiffinReview'>) {
  const t = useT();
  const pick = useLocalized();
  const scheduleDays = useScheduleDays();
  const draft = useTiffinStore();
  const { data: config } = useAppConfig();
  const { data: methods } = usePaymentMethods();

  // Re-quote on arrival: the price confirmed is the current one.
  const fresh = useTiffinQuote(draft.quoteRequest());
  useEffect(() => {
    if (fresh.data) draft.setQuote(fresh.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh.data]);

  const createOrder = useCreateTiffinOrder();
  const initiatePayment = useInitiatePayment();

  const quote = draft.quote;
  const total = draft.authoritativeTotal();
  const window = config?.tiffin?.slots.find((slot) => slot.slot === draft.serviceSlot);
  const delivery = draft.fulfilmentType === 'DOORSTEP_DELIVERY';
  const method = methods?.find((m) => m.code === draft.paymentMethodCode);
  const busy = createOrder.isPending || initiatePayment.isPending || fresh.isFetching;
  const canConfirm = Boolean(total) && Boolean(draft.paymentMethodCode) && !busy;

  const onConfirm = async () => {
    if (!canConfirm) return;
    try {
      const order = await createOrder.mutateAsync(toCreateTiffinRequest(draft));
      const intent = await initiatePayment.mutateAsync({
        donationId: order.id,
        paymentMethodCode: draft.paymentMethodCode!,
        walletMsisdn:
          draft.paymentMethodCode === 'easypaisa' ? draft.walletMsisdn ?? undefined : undefined,
        returnUrl: paymentReturnUrl(),
      });
      // Paid somewhere else — on a phone or a provider's page — is not paid
      // yet. Wait for the provider rather than confirm an unpaid order.
      if (intent.status === 'PROCESSING' || (intent.status === 'PENDING' && !intent.instructions)) {
        navigation.navigate('PaymentPending', {
          paymentId: intent.paymentId,
          method: draft.paymentMethodCode!,
          orderType: 'TIFFIN',
          donationId: order.id,
          publicReference: order.publicReference,
          amount: order.totalAmount,
          instrumentLabel: intent.instrumentLabel,
          simulated: intent.simulated,
          redirectUrl: intent.redirectUrl,
          testMode: intent.testMode,
        });
        return;
      }
      navigation.navigate('TiffinConfirmation', {
        orderId: order.id,
        publicReference: order.publicReference,
      });
    } catch {
      // Shown below; the draft is kept so nothing has to be entered again.
    }
  };

  const failure = createOrder.error ?? initiatePayment.error ?? fresh.error;
  const address = [draft.address.addressLine, draft.address.area, draft.address.city, draft.address.landmark]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-tiffin-review"
      subHeader={
        <CheckoutHeader
          current={5}
          steps={TIFFIN_STEPS}
          title={t('Review Your Order')}
          subtitle={t('Please check your order before confirming')}
        />
      }
      pinnedFooter={
        <View style={styles.pinned}>
          <View style={styles.totalBar}>
            <Glyph name="privacy" size={18} />
            <Text variant="bodyStrong" tone="brand" style={styles.totalLabel}>{t('Total')}</Text>
            <Text variant="amountSmall" tone="brand" testID="tiffin-review-total">
              {total ? formatMoney(total) : '—'}
            </Text>
          </View>
          {failure ? (
            <Text variant="caption" tone="danger" align="center" testID="tiffin-review-error">
              {t(failure.message)}
            </Text>
          ) : null}
          <Button
            label={t('Place Order')}
            onPress={() => void onConfirm()}
            disabled={!canConfirm}
            loading={createOrder.isPending || initiatePayment.isPending}
            leadingIcon={<LockIcon />}
            testID="cta-tiffin-confirm"
          />
        </View>
      }
    >
      <Card
        title={t('Your tiffin')}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            onPress={() => navigation.navigate('TiffinMenu')}
            testID="tiffin-review-edit-box"
          />
        }
        testID="tiffin-review-box"
      >
        <CardRow label={t('Meal')} value={window ? t(window.label) : '—'} />
        {window ? <CardRow label={t('Served')} value={formatWindow(window.start, window.end)} /> : null}
        {quote ? (
          <>
            <CardRow
              label={t('How often')}
              value={`${t(serviceScheduleLabel[quote.schedule])} · ${scheduleDays(quote.schedule, quote.serviceDate)}`}
              testID="tiffin-review-schedule"
            />
            <CardRow
              label={t('Days')}
              value={`${formatDays(quote.days)} · ${formatDayRange(quote.serviceDate, quote.endDate)}`}
              testID="tiffin-review-days"
            />
            <CardRow label={t('Meals')} value={formatMeals(quote.deliveries)} />
            <CardRow label={t('Persons')} value={formatPeople(quote.persons)} />
          </>
        ) : null}
        <CardDivider />
        <Text variant="label" tone="secondary" style={styles.boxLabel}>
          {t('In each box')}
        </Text>
        {(quote?.items ?? []).map((line) => (
          <View key={line.menuItemId ?? line.name} style={styles.line} testID={`tiffin-review-line-${line.menuItemId}`}>
            <Text variant="body" style={styles.lineName}>
              {pick(line.name, line.nameUr)}
              <Text variant="body" tone="secondary">{`  ×${line.quantity}`}</Text>
            </Text>
            <Text variant="body">{formatMoney(line.lineTotal)}</Text>
          </View>
        ))}
        <CardDivider />
        {quote ? (
          <>
            <CardRow label={t('One box')} value={formatMoney(quote.boxTotal)} />
            <CardRow
              label={`× ${formatPeople(quote.persons)} × ${formatMeals(quote.deliveries)}`}
              value={formatMoney(quote.subtotal)}
            />
            {delivery ? (
              <CardRow
                label={`${t('Pick & drop')} × ${formatMeals(quote.deliveries)}`}
                value={formatMoney(quote.deliveryFee)}
              />
            ) : null}
            <CardRow label={t('Total')} value={formatMoney(quote.totalAmount)} emphasis />
          </>
        ) : null}
      </Card>

      <Card
        title={t('Contact Person')}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            onPress={() => navigation.navigate('TiffinContact')}
            testID="tiffin-review-edit-contact"
          />
        }
        style={styles.card}
        testID="tiffin-review-contact"
      >
        <CardRow label={t('Name')} value={draft.customer.fullName || '—'} />
        <CardRow label={t('Mobile')} value={draft.customer.mobileNumber || '—'} />
        {draft.customer.email ? <CardRow label={t('Email')} value={draft.customer.email} /> : null}
      </Card>

      <Card
        title={t(draft.fulfilmentType ? tiffinFulfilmentLabel[draft.fulfilmentType] : 'Delivery')}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            onPress={() => navigation.navigate('TiffinDelivery')}
            testID="tiffin-review-edit-delivery"
          />
        }
        style={styles.card}
        testID="tiffin-review-route"
      >
        {delivery ? (
          <CardRow label={t('Deliver to')} value={address || '—'} />
        ) : (
          <CardRow label={t('Collect from')} value={config?.tiffin?.pickup.address ?? '—'} />
        )}
        {draft.note.trim() ? <CardRow label={t('Note')} value={draft.note.trim()} /> : null}
      </Card>

      <Card
        title={t('Payment')}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            onPress={() => navigation.navigate('TiffinPayment')}
          />
        }
        style={styles.card}
      >
        <CardRow label={t('Method')} value={method ? pick(method.name, method.nameUr) : '—'} />
      </Card>
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  card: {
    marginTop: space.lg,
  },
  boxLabel: {
    marginTop: space.sm,
    marginBottom: space.xs,
  },
  line: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: space.md,
    paddingVertical: space.xs,
  },
  lineName: {
    flex: 1,
  },
  pinned: {
    gap: space.sm,
  },
  totalBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.brandSubtle,
  },
  totalLabel: {
    flex: 1,
  },
});
